import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { RefreshCw, ScrollText, Search } from 'lucide-react';
import mcpServerService from '../../../services/mcpServerService';
import PaginationBar from '../../PlatformAdmin/PaginationBar';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from '../McpServerDetail.module.scss';
import {
  LOG_RETENTION_DAYS,
  describeError,
  formatLogTime,
  getLogStatusMeta,
  getLogStatusOptions,
  unwrapList,
} from '../mcpUtils';

const PAGE_SIZE = 50;

const TONE_CLASS = {
  success: styles.success,
  danger: styles.danger,
  warning: styles.warning,
  info: styles.info,
  neutral: styles.neutral,
};

const StatusPill = ({ status, server }) => {
  const { tone, label, title } = getLogStatusMeta(status, server);
  const pill = <span className={`${styles.pill} ${TONE_CLASS[tone]}`}>{label}</span>;
  return title ? <Tooltip title={title}>{pill}</Tooltip> : pill;
};

/**
 * Call logs (§9).
 *
 * `reference` is searchable because it is the correlation id a caller was shown
 * in an error message — it is how a customer will report a problem ("my agent
 * got reference a1b2c3"), and matching that back to a row is the whole point.
 */
const CallLogsTab = ({ server, notify }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (status !== 'all') params.status = status;
      const data = await mcpServerService.listLogs(server.id, params);
      setLogs(unwrapList(data, 'logs'));
      setTotal(typeof data?.total === 'number' ? data.total : 0);
      setError(null);
    } catch (e) {
      setError(describeError(e, "Couldn't load the call log."));
    } finally {
      setLoading(false);
    }
  }, [server.id, offset, status]);

  useEffect(() => {
    load();
  }, [load]);

  // Reference search is client-side over the loaded page: the customer is
  // looking for one specific id they were handed, not browsing.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) =>
      `${l.reference || ''} ${l.tool_name || ''}`.toLowerCase().includes(q)
    );
  }, [logs, search]);

  return (
    <>
      <div className={sharedStyles.toolbar}>
        <div className={sharedStyles.filterGroup}>
          <TextField
            size="small"
            placeholder="Search by reference or tool"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <Search size={16} style={{ marginRight: 8 }} /> }}
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setOffset(0);
              }}
            >
              {getLogStatusOptions(server).map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <Button
          className={sharedStyles.outlineBtn}
          variant="outlined"
          size="small"
          startIcon={<RefreshCw size={15} />}
          onClick={load}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      ) : visible.length === 0 ? (
        <div className={sharedStyles.emptyState}>
          <ScrollText size={44} className={sharedStyles.emptyIcon} />
          <Typography variant="h6">
            {search || status !== 'all' ? 'No matching calls' : `No calls in the last ${LOG_RETENTION_DAYS} days`}
          </Typography>
          <Typography variant="body2" className={sharedStyles.muted}>
            {search || status !== 'all'
              ? 'Try a different reference or status.'
              : `Call logs are kept for ${LOG_RETENTION_DAYS} days — anything older has aged out.`}
          </Typography>
        </div>
      ) : (
        <>
          <div className={sharedStyles.tableCard}>
            <div className={`${styles.logRow} ${styles.logHeadRow}`}>
              <span>Time</span>
              <span>Tool</span>
              <span>Status</span>
              <span>Latency</span>
              <span>Upstream</span>
              <span>Reference</span>
            </div>
            {visible.map((log) => (
              <div key={log.id} className={styles.logRow}>
                <span className={sharedStyles.muted}>{formatLogTime(log.created_at)}</span>
                <span className={styles.toolName}>{log.tool_name || '—'}</span>
                <span>
                  <StatusPill status={log.status} server={server} />
                </span>
                <span className={sharedStyles.muted}>
                  {log.latency_ms !== undefined && log.latency_ms !== null ? `${log.latency_ms} ms` : '—'}
                </span>
                <span className={sharedStyles.muted}>{log.upstream_status ?? '—'}</span>
                <span>
                  {log.reference ? <code className={styles.reference}>{log.reference}</code> : '—'}
                </span>
                {log.error && <span className={styles.logError}>{log.error}</span>}
              </div>
            ))}
          </div>

          {total > PAGE_SIZE && (
            <PaginationBar
              total={total}
              limit={PAGE_SIZE}
              offset={offset}
              loading={loading}
              unit="calls"
              onOffsetChange={setOffset}
            />
          )}
        </>
      )}
    </>
  );
};

export default CallLogsTab;
